'use client'

import type {
  Instrument,
  ProposalOrder,
} from '@moment/horizon-data-access/proposal'
import type { QueryClient } from '@tanstack/react-query'
import type { SecurityInventoryItem } from '@/shared/sorm/security/security-row-adapter'
import type { LiveInventoryReturnValues } from '@/types/api/live-inventory'

import { useFeatureFlag } from '@moment/app-config-ui'
import { IsoDateStr } from '@moment/domain-types'
import { toError } from '@moment/observability/logger'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

import { ACTIONS_TABLE_FROM_SORM } from '@/app/feature-flags'
import { useUserRiskGroupId } from '@/hooks/use-user-risk-group-id'
import { createLogger } from '@/lib/logger'
import { useIsUsingEvalsAsOrders } from '@/portfolio/proposal-builder/hooks/use-is-using-evals-as-orders'
import { fetchSecurityInventoryItems } from '@/shared/sorm/security/fetch-security-inventory-items'
import { validRiskGroupId } from '@/shared/sorm/security/validate-risk-group'

const LOGGER = createLogger(
  'portfolio/proposal-builder/hooks/use-order-security-instruments',
)

const ORDERS_GROUP = 'orders'

/** The order instrument's reference-data fields that Security serves, with the org's overlays. */
const ORDER_INSTRUMENT_RETURNS: LiveInventoryReturnValues[] = [
  'isin',
  'cusip',
  'description',
  'description_short',
  'ticker',
  'issuer',
  'sector',
  'sp_rating',
  'moodys_rating',
  'lowest_rating',
  'municipal_state',
  'maturity_date',
]

/** What the account holdings table shows per bond: the order fields plus its type, duration and evals. */
const HOLDING_INSTRUMENT_RETURNS: LiveInventoryReturnValues[] = [
  ...ORDER_INSTRUMENT_RETURNS,
  'type',
  'duration',
  'eval_price',
  'eval_ytw',
]

type OrderSecurityItems = ReadonlyMap<string, SecurityInventoryItem>

interface OrderSecurityReadContext {
  riskGroupId: string
  useEvalsAsOrders: boolean
}

/** Whether, and with what, order instrument fields come from SORM Security instead of the order snapshot. */
type OrderSecurityRead =
  | { status: 'disabled' }
  | { status: 'loading' }
  | { status: 'ready'; context: OrderSecurityReadContext }
  | { status: 'failed'; error: Error }

const RISK_GROUPS_FAILED = new Error(
  'Risk groups failed to load for Security reads',
)
const NO_RISK_GROUP = new Error('No risk group to read Security with')

/** What a Security read of a proposal's ordered bonds needs, outside the proposal builder. */
export function useOrderSecurityRead(): OrderSecurityRead {
  const enabled = useFeatureFlag(ACTIONS_TABLE_FROM_SORM)
  const { riskGroupId, riskGroups, isLoading, isError } = useUserRiskGroupId()
  const useEvalsAsOrders = useIsUsingEvalsAsOrders()
  const validId = validRiskGroupId(riskGroupId, riskGroups)
  return useMemo((): OrderSecurityRead => {
    if (!enabled) return { status: 'disabled' }
    if (isError) return { status: 'failed', error: RISK_GROUPS_FAILED }
    if (validId) {
      return {
        status: 'ready',
        context: { riskGroupId: validId, useEvalsAsOrders },
      }
    }
    if (isLoading) return { status: 'loading' }
    return { status: 'failed', error: NO_RISK_GROUP }
  }, [enabled, isError, isLoading, validId, useEvalsAsOrders])
}

const READS = {
  orders: {
    returns: ORDER_INSTRUMENT_RETURNS,
    queryName: 'proposal_order_instruments',
  },
  holdings: {
    returns: HOLDING_INSTRUMENT_RETURNS,
    queryName: 'proposal_account_holding_instruments',
  },
} as const

/** Which projection to read: a proposal's ordered bonds, or an account's held and ordered bonds. */
type SecurityItemsRead = keyof typeof READS

/**
 * Security rows by ISIN, in one batch-query. Like the actions table, the bonds
 * are read whatever the org's reference-data entitlements, so none drops out.
 */
export function securityItemsQuery(
  queryClient: QueryClient,
  isins: ReadonlyArray<string>,
  context: OrderSecurityReadContext,
  read: SecurityItemsRead,
) {
  const uniqueIsins = [...new Set(isins)].sort()
  const { returns, queryName } = READS[read]
  return queryOptions({
    queryKey: [
      'proposal-orders',
      'security-instruments',
      read,
      context,
      uniqueIsins,
    ],
    queryFn: async ({ signal }): Promise<OrderSecurityItems> => {
      const itemsByKey = await fetchSecurityInventoryItems(queryClient, {
        ...context,
        groups: [{ key: ORDERS_GROUP, isins: uniqueIsins, side: 'ask' }],
        returns,
        queryName,
        signal,
      })
      return new Map(
        (itemsByKey.get(ORDERS_GROUP) ?? []).map((item) => [
          String(item.isin),
          item,
        ]),
      )
    },
  })
}

/** Security rows by ISIN when the gate is on; `disabled` means callers keep their snapshots. */
type SecurityItemsByIsin =
  | { status: 'disabled' }
  | { status: 'loading' }
  | { status: 'ready'; items: OrderSecurityItems }
  | { status: 'failed'; error: Error }

const NO_CONTEXT: OrderSecurityReadContext = {
  riskGroupId: '',
  useEvalsAsOrders: false,
}
const NO_ITEMS: OrderSecurityItems = new Map()

/** Reads `isins` from Security behind the gate, logging a failed read or missing context once. */
export function useSecurityItemsByIsin(
  isins: ReadonlyArray<string>,
  read: SecurityItemsRead,
): SecurityItemsByIsin {
  const queryClient = useQueryClient()
  const securityRead = useOrderSecurityRead()
  const context = securityRead.status === 'ready' ? securityRead.context : null
  const { data, error: queryError } = useQuery({
    ...securityItemsQuery(queryClient, isins, context ?? NO_CONTEXT, read),
    enabled: context !== null && isins.length > 0,
  })

  const state = useMemo((): SecurityItemsByIsin => {
    switch (securityRead.status) {
      case 'disabled':
      case 'loading':
      case 'failed':
        return securityRead
      case 'ready':
        if (queryError) return { status: 'failed', error: queryError }
        if (isins.length === 0) return { status: 'ready', items: NO_ITEMS }
        return data ? { status: 'ready', items: data } : { status: 'loading' }
      default:
        return securityRead satisfies never
    }
  }, [securityRead, isins.length, data, queryError])

  const error = state.status === 'failed' ? state.error : null
  // Syncs with the logging backend.
  useEffect(() => {
    if (error) {
      LOGGER.error('Failed to read instruments from Security', {
        read,
        error: toError(error),
      })
    }
  }, [error, read])

  return state
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function isoDate(value: unknown): IsoDateStr | null {
  const parsed = IsoDateStr.safeParse(value)
  return parsed.success ? parsed.data : null
}

/**
 * The order with its instrument's reference-data fields read from its Security
 * row, nulls included. Without a row the order keeps its own snapshot, as the
 * actions table does.
 */
export function withSecurityInstrument<
  Order extends Pick<ProposalOrder, 'instrument_id' | 'instrument'>,
>(order: Order, items: OrderSecurityItems): Order {
  const item = items.get(order.instrument_id)
  if (!item || !order.instrument) return order
  const instrument: Instrument = {
    ...order.instrument,
    cusip: text(item.cusip),
    description: text(item.description) ?? '',
    description_short: text(item.description_short) ?? undefined,
    ticker: text(item.ticker),
    issuer: text(item.issuer) ?? '',
    sector: text(item.sector) ?? undefined,
    sp_rating: text(item.sp_rating),
    moodys_rating: text(item.moodys_rating),
    lowest_rating: text(item.lowest_rating),
    municipal_state: text(item.municipal_state),
    maturity_date: isoDate(item.maturity_date) ?? undefined,
  }
  return { ...order, instrument }
}
